import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppDispatchContext, AppStateContext } from '../../context/AppContext';
import { useHandleEnter } from '../../utils/hooks';
import pivCardReaderImg from '../../images/piv-card-reader.svg';
import axiadAppImg from '../../images/axiadid-app.svg';
import appleIcon from '../../images/apple-icon.svg';
import androidIcon from '../../images/android-icon.svg';

const cardStyle = {
    width: 240,
    padding: '16px 24px',
    background: '#C8EFFA',
    borderRadius: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
};

const cardImageWrapStyle = {
    width: 160,
    height: 160,
    borderRadius: 24,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const cardCaptionStyle = {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '20px',
    color: '#171818',
    margin: 0,
};

const sponsorDeviceConfig = {
    piv: {
        image: pivCardReaderImg,
        alt: 'PIV card inserted in reader',
        captionKey: 'pivGuidanceModal$pivCard',
    },
};

const getDefaultSponsorConfig = () => ({
    image: null,
    alt: '',
    captionKey: 'pivGuidanceModal$sponsorDevice',
});

const platformBadgeStyle = {
    width: 24,
    height: 24,
};

const PivSetupGuidanceModal = () => {
    const dispatch = useContext(AppDispatchContext);
    const { modal: { closeAction, sponsorDeviceType, availablePlatforms } } = useContext(AppStateContext);
    const { t } = useTranslation();

    const close = () => {
        closeAction && closeAction();
        dispatch({ type: 'CLEAR_MODAL' });
    };
    useHandleEnter({ action: close });

    const sponsorConfig = sponsorDeviceType
        ? (sponsorDeviceConfig[sponsorDeviceType] || getDefaultSponsorConfig())
        : null;

    const iosOnly = availablePlatforms?.ios && !availablePlatforms?.android;
    const androidOnly = availablePlatforms?.android && !availablePlatforms?.ios;

    const axiadAppCaptionKey = iosOnly
        ? 'pivGuidanceModal$axiadAppIos'
        : androidOnly
        ? 'pivGuidanceModal$axiadAppAndroid'
        : 'pivGuidanceModal$axiadApp';

    return (
        <div style={{ padding: 26 }}>
            <div style={{ marginBottom: 16 }}>
                <h5 style={{ fontSize: 20, fontWeight: 700, lineHeight: '30px', letterSpacing: 0.2, color: '#171818', margin: 0 }}>
                    {t('pivGuidanceModal$title')}
                </h5>
            </div>
            <p style={{ fontSize: 14, color: '#171818', lineHeight: '20px', marginBottom: 24 }}>
                {t('pivGuidanceModal$subtitle')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 24 }}>
                {sponsorConfig && (
                    <div style={cardStyle} data-test-id="sponsorDeviceCard" data-sponsor-type={sponsorDeviceType}>
                        {sponsorConfig.image && (
                            <div style={cardImageWrapStyle}>
                                <img src={sponsorConfig.image} alt={sponsorConfig.alt} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                        )}
                        <p style={cardCaptionStyle}>{t(sponsorConfig.captionKey)}</p>
                    </div>
                )}
                <div style={cardStyle} data-test-id="axiadAppCard">
                    <div style={{ position: 'relative' }}>
                        <div style={cardImageWrapStyle}>
                            <img src={axiadAppImg} alt="Axiad ID mobile app" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                        {availablePlatforms && (availablePlatforms.ios || availablePlatforms.android) && (
                            <div style={{ position: 'absolute', bottom: 10, right: -24, display: 'flex', gap: 4 }}>
                                {availablePlatforms.ios && (
                                    <img src={appleIcon} alt="iOS" style={platformBadgeStyle} data-test-id="platformBadgeIos" />
                                )}
                                {availablePlatforms.android && (
                                    <img src={androidIcon} alt="Android" style={platformBadgeStyle} data-test-id="platformBadgeAndroid" />
                                )}
                            </div>
                        )}
                    </div>
                    <p style={cardCaptionStyle}>{t(axiadAppCaptionKey)}</p>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, lineHeight: '20px' }}>
                    <span style={{ color: 'rgba(0, 15, 27, 0.60)' }}>{t('pivGuidanceModal$helpText')} </span>
                    <a
                        href={t('pivGuidanceModal$supportUrl')}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-test-id="pivGuidanceSupportLink"
                        style={{ color: '#0056E7' }}
                    >
                        {t('pivGuidanceModal$docLink')}
                    </a>
                </span>
                <button
                    type="button"
                    onClick={close}
                    data-test-id="pivGuidanceCloseBtn"
                    style={{
                        height: 36,
                        padding: '8px 12px',
                        borderRadius: 4,
                        border: '1px solid #0062E6',
                        color: '#0062E6',
                        background: 'transparent',
                        fontSize: 14,
                        lineHeight: '16px',
                        cursor: 'pointer',
                    }}
                >
                    {t('pivGuidanceModal$continue')}
                </button>
            </div>
        </div>
    );
};

export default PivSetupGuidanceModal;
